import React, { MouseEventHandler, Ref, useRef, useState, WheelEventHandler } from 'react'
import { isDesktop } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { SafeHtml } from '../SafeHtml'
import { useStaticText, DescriptionItem } from '../../contexts/staticTextContext'
import { useContextUI } from '../../contexts/UIContext'
import { useEnv } from '../../utils/useEnv'
import { ButtonWrapper, Container, GalleryWrapper } from './common'
import CloseButton from '../CloseButton'
import { UIElement } from '../../types/UIElements'

const Gallery = () => {
  const { t, i18n } = useTranslation()
  const { isKiosk } = useEnv()
  const { galleryData } = useStaticText()
  const { filterOutUiElement } = useContextUI()
  const containerRef = useRef<HTMLDivElement>()
  const [wrapperClass, setWrapperClass] = useState<string>('fade-in')
  const [fadeOut, setFadeOut] = useState<boolean>(false)

  const lang = i18n.language

  const handleHorizontalWheelScroll: WheelEventHandler<HTMLDivElement> = event => {
    if (!containerRef?.current) return
    event.stopPropagation()
    containerRef.current.scrollLeft += event.deltaY
  }

  const handleClickOutside: MouseEventHandler<HTMLDivElement> = e => {
    if (e.target !== containerRef.current) return
    handleClose()
  }

  const handleClose = () => {
    setWrapperClass('fade-out')
    setFadeOut(true)
    window.history.replaceState(null, '', '/')
    setTimeout(() => {
      filterOutUiElement(UIElement.GALLERY)
      setFadeOut(false)
    }, 500)
  }

  return (
    <>
      <GalleryWrapper
        ref={containerRef as Ref<HTMLDivElement>}
        onWheel={isDesktop ? handleHorizontalWheelScroll : undefined}
        onClick={handleClickOutside}
        className={wrapperClass}
      >
        <Container>
          <div className={`wrapper ${fadeOut ? 'fade-out' : 'fade-in'}`}>
            {galleryData.map((gallery, galleryIndex) => (
              <React.Fragment key={galleryIndex}>
                {(gallery[`description_${lang}` as keyof typeof gallery] as DescriptionItem[])
                  ?.length > 0 && (
                  <div
                    key={galleryIndex}
                    className={
                      galleryIndex === 0 ? 'about-gallery' : 'text-container about-gallery'
                    }
                  >
                    <main>
                      {galleryIndex === 0 && (
                        <>
                          <h1 className="title">{t('gallery.title')}</h1>
                          <h2 className="subtitle">
                            {gallery[`title_${lang}` as keyof typeof gallery] || '\u00A0'}
                          </h2>
                        </>
                      )}
                      {galleryIndex !== 0 && (
                        <>
                          <h1 className="title">
                            {gallery[`title_${lang}` as keyof typeof gallery] || '\u00A0'}
                          </h1>

                          <h2 className="subtitle">
                            {(
                              gallery[
                                `description_${lang}` as keyof typeof gallery
                              ] as DescriptionItem[]
                            )?.[0]?.notes?.toLowerCase() === 'h'
                              ? (
                                  gallery[
                                    `description_${lang}` as keyof typeof gallery
                                  ] as DescriptionItem[]
                                )?.[0]?.text
                              : '\u00A0'}
                          </h2>
                        </>
                      )}
                      {(
                        gallery[`description_${lang}` as keyof typeof gallery] as DescriptionItem[]
                      )?.map((item, index) => {
                        if (index === 0 && item.notes?.toLowerCase() === 'h') return null
                        const textWithBreaks = item.text?.replace(/\n/g, '<br />')
                        return (
                          <SafeHtml
                            key={index}
                            html={textWithBreaks || ''}
                            allowedTags={['a', 'b', 'br']}
                            allowedAttrs={['href']}
                            disableLinks={isKiosk}
                            className={`description${
                              item.notes?.toLowerCase() === 'm' ? ' addMargin' : ''
                            }`}
                          />
                        )
                      })}
                    </main>
                  </div>
                )}
                {gallery.multimedia && gallery.multimedia.length > 0 && (
                  <div className="generated-pattern-container">
                    {gallery.multimedia.map((media, mediaIndex) => (
                      <img
                        key={mediaIndex}
                        className="generated-pattern"
                        src={media.multimediaUri}
                        alt="generated pattern"
                      />
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Container>
      </GalleryWrapper>
      <ButtonWrapper>
        <CloseButton onClick={handleClose} className={wrapperClass} />
      </ButtonWrapper>
    </>
  )
}

export default Gallery
